// ExtrasForm.js - Premium Design
import React, { useState } from 'react';
import { EXTRAS_PRICING } from '../../utils/extrasConfig';
import { BOOKING_TYPES } from '../../utils/constants';

const ExtrasForm = ({ searchData, initialData, onContinue, onBack, onChange, currency = 'USD' }) => {
  const [formData, setFormData] = useState(initialData || {
    flightNumber: '',
    childSeats: 0,
    boosterSeats: 0,
    outwardNotes: '',
    returnNotes: '',
    meetGreet: false
  });

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    if (onChange) onChange(newFormData);
  };

  const isAirportBooking = () => {
    return searchData?.type === BOOKING_TYPES.AIRPORT_TRANSFER || 
          searchData?.type === BOOKING_TYPES.AIRPORT_HOURLY;
  };

  const calculateExtrasTotal = () => {
    let total = 0;
    total += (formData.childSeats || 0) * EXTRAS_PRICING.childSeat;
    total += (formData.boosterSeats || 0) * EXTRAS_PRICING.boosterSeat;
    if (formData.meetGreet) total += EXTRAS_PRICING.meetGreet;
    return total;
  };

  const handleSubmit = () => {
    if (isAirportBooking() && !formData.flightNumber.trim()) {
      alert('Please enter your flight number for airport transfers');
      return;
    }
    const extrasData = { ...formData, extrasTotal: calculateExtrasTotal() };
    onContinue(extrasData);
  };

  const getCurrencySymbol = () => {
    const symbols = { EUR: '€', USD: '$', GBP: '£', LKR: 'Rs' };
    return symbols[currency] || currency;
  };

  const inputClasses = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600 text-sm";
  const labelClasses = "block text-xs font-black text-blue-500 uppercase tracking-[0.2em] mb-3 pl-1";

  return (
    <div className="space-y-10">
      <div className="flex items-center gap-4">
        <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">Extras & Notes</h2>
        <div className="h-px flex-1 bg-white/10"></div>
      </div>

      {/* Flight Number */}
      {isAirportBooking() && (
        <div className="glass-panel p-6 rounded-3xl border-white/5">
          <label className={labelClasses}>
            Flight Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">✈️</span>
            <input
              type="text"
              placeholder="e.g. EK650 or CMB Arrival"
              className={`${inputClasses} pl-12 font-bold tracking-widest uppercase`}
              value={formData.flightNumber}
              onChange={(e) => handleInputChange('flightNumber', e.target.value)}
            />
          </div>
          <p className="text-[10px] text-gray-500 mt-2 font-medium tracking-wide">
            Our driver will monitor your flight status for real-time pickup adjustments.
          </p>
        </div>
      )}

      {/* Child Seats Selection */}
      <div className="space-y-6">
        <label className={labelClasses}>Child Safety Equipment</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Child Seat */}
          <div className={`glass-panel p-5 rounded-2xl border-white/5 transition-all ${formData.childSeats > 0 ? 'bg-blue-600/5 border-blue-500/30' : ''}`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-2xl">👶</div>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">Child Seat</p>
                <p className="text-[10px] text-gray-500 font-medium">0-18 kg (approx 0-4 yrs)</p>
              </div>
              <div className="text-xs font-black text-blue-400 bg-blue-400/10 px-2 py-1 rounded">
                +{getCurrencySymbol()}{EXTRAS_PRICING.childSeat}
              </div>
            </div>
            <select 
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white outline-none focus:border-blue-500 transition-all font-bold"
              value={formData.childSeats}
              onChange={(e) => handleInputChange('childSeats', parseInt(e.target.value))}
            >
              {[0, 1, 2, 3].map(n => (
                <option key={n} value={n} className="bg-[#0f172a]">{n === 0 ? 'No child seat' : `${n} Child Seat${n > 1 ? 's' : ''}`}</option>
              ))}
            </select>
          </div>

          {/* Booster Seat */}
          <div className={`glass-panel p-5 rounded-2xl border-white/5 transition-all ${formData.boosterSeats > 0 ? 'bg-blue-600/5 border-blue-500/30' : ''}`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gold bg-[#eab308]/10 flex items-center justify-center text-2xl">🪑</div>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">Booster Seat</p>
                <p className="text-[10px] text-gray-500 font-medium">15-36 kg (approx 4-10 yrs)</p>
              </div>
              <div className="text-xs font-black text-blue-400 bg-blue-400/10 px-2 py-1 rounded">
                +{getCurrencySymbol()}{EXTRAS_PRICING.boosterSeat}
              </div>
            </div>
            <select 
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white outline-none focus:border-blue-500 transition-all font-bold"
              value={formData.boosterSeats}
              onChange={(e) => handleInputChange('boosterSeats', parseInt(e.target.value))}
            >
              {[0, 1, 2, 3].map(n => (
                <option key={n} value={n} className="bg-[#0f172a]">{n === 0 ? 'No booster seat' : `${n} Booster Seat${n > 1 ? 's' : ''}`}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Driver Notes */}
      <div className="space-y-4">
        <label className={labelClasses}>Message for Chauffeur</label>
        <textarea
          placeholder="Luggage details, special requests, or exact meeting instructions..."
          rows={4}
          className={`${inputClasses} resize-none py-4 leading-relaxed`}
          value={formData.outwardNotes}
          onChange={(e) => handleInputChange('outwardNotes', e.target.value)}
        />
        {searchData?.showReturn && (
          <textarea
            placeholder="Notes for your return journey..."
            rows={4}
            className={`${inputClasses} resize-none py-4 leading-relaxed mt-4`}
            value={formData.returnNotes}
            onChange={(e) => handleInputChange('returnNotes', e.target.value)}
          />
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-6">
        <button
          onClick={onBack}
          className="flex-1 py-4 border border-white/10 rounded-full font-black text-xs tracking-widest uppercase text-gray-400 hover:text-white hover:bg-white/5 transition-all active:scale-95"
        >
          ← Back
        </button>
        <button
          id="extras-continue-btn"
          onClick={handleSubmit}
          className="flex-1 py-4 bg-blue-600 text-white rounded-full font-black text-xs tracking-widest uppercase hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-95"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default ExtrasForm;